import { extractLanguage } from './lang/lang';
import { trace } from './setup/trace';
import { reportErrorToSentry } from './setup/sentry';
import { store } from './setup/store';
import { getLocale } from './lang/langSlice';
import { configureFetchDisplayMessages } from './lang/services';
import { config } from '../config';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from './login/loginSlice';
import { accessTokenStored, idTokenStored } from './tokenHandling/tokenSlice';
import { accessToken } from './tokenHandling/accessToken';
import {
    configureMockUserManager,
    configureUserManager,
    createUserManager,
    type SessionRenewedResult,
} from './login/login';
import type { User, UserManager } from 'oidc-client-ts';
import { attemptInitialSignIn } from './setup/oauth';

export interface OAuthConfig {
    onSessionExpired: () => void;
    onSessionRenewed: (result: SessionRenewedResult) => void;
}

export const main = async (renderApp: (user?: User | undefined | null) => void) => {
    const fetchDisplayMessages = configureFetchDisplayMessages(store);

    // We want the `<html lang>` attribute to be synced with the
    // language currently displayed
    store.subscribe(() => {
        const lang = extractLanguage(getLocale(store.getState()));
        const html = document.querySelector('html');

        if (html && lang && html.getAttribute('lang') !== lang) {
            html.setAttribute('lang', lang);
        }
    });
    // You will need to get the user language yourself then
    // you may fetch the suitable messages. Depending
    // on when and from where you fetch the user settings you might
    // want to employ a loading spinner while the request is ongoing.
    await fetchDisplayMessages();

    if (config.isTestTenantMode) {
        // you have to log in with azure idp to be able to create a test account
        // we've added this, since you can book services for free as a test tenant
        const oauthConfig: OAuthConfig = {
            onSessionExpired: () => {
                accessToken.discardAccessToken();
                store.dispatch(userSessionExpired());
            },
            onSessionRenewed: (result: SessionRenewedResult) => {
                trace('index.onTokenRenewed', result);

                accessToken.saveAccessToken(result.accessToken);
                store.dispatch(accessTokenStored(result.accessToken));
                store.dispatch(idTokenStored(result.idToken));
                store.dispatch(userProfileObtained(result.profile));

                store.dispatch(userSessionRenewed());
            },
        };

        const userManager: UserManager = config.login.mockAuthorization
            ? configureMockUserManager(oauthConfig)
            : configureUserManager(oauthConfig, createUserManager());

        try {
            await userManager.clearStaleState();
            const user = await attemptInitialSignIn(userManager);
            renderApp(user);
        } catch (error) {
            trace('could not start application', error);
            reportErrorToSentry(error);
        }
    } else {
        try {
            renderApp();
        } catch (error) {
            trace('could not start application', error);
            reportErrorToSentry(error);
        }
    }
};
